import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { filter, finalize, map } from "rxjs/operators";
import {
	BpModalCanDismiss,
	Cryptocoin,
	ErrorClass,
	Fee,
	Fiat,
	FiatWallet,
	ForbiddenError,
	Limit,
	PaymentOption
} from "@models";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { CryptocoinService } from "@main-services/currency/cryptocoin.service";
import { ModalHeader } from "@models/modal_step";
import { FiatTransaction } from "@models/transaction";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { Tradable } from "@models/interfaces/tradable";
import { FiatTransactionModal } from "../fiat-transaction.modal";
import { CryptowalletDetailsComponent } from "../../../wallet-edit/wallet-details/cryptowallet-details.component";
import { Helper } from "@helpers/functions";
import { truncateFixed } from "@helpers/utils";
import { TOKEN_BEST_ID, VERIFICATION_MODAL_ROUTE } from "@helpers/constants";
import { ApicallOption } from "@core-services/apicall.service";
import { TranslationService } from "@core-services/translation.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { Best2TermsModalComponent } from "@modules/trade/modals/best-2-terms-modal/components/best-2-terms-modal.component";
import { AccountService } from "@modules/account/services/account.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { DepositPendingComponent } from "@modules/main/components/step-summary/success-screens/deposit-pending/deposit-pending.component";
import { Paysafe3DSModalComponent } from "@modules/paysafe/components/paysafe-3ds-modal/paysafe-3ds-modal.component";
import { config } from "@config";
import { BestFacade } from "@modules/new_account/features/my-best/domain/+state/best.facade";
import { BestModel } from "@modules/new_account/features/my-best/domain/entities/best.model";
import { MasterdataService } from "@core-services/masterdata.service";
import { DepositSurveyService } from "@modules/+new/payments/features/deposit-survey/infrastructure/deposit-survey.service";
import { VerificationModalFacade } from "@modules/verification/modal/state/verification-modal.facade";

@UntilDestroy()
@Component({
	templateUrl: "./fiat-deposit-modal.component.html",
	styleUrls: ["./fiat-deposit-modal.component.scss"],
	standalone: false
})
export class FiatDepositModalComponent
	extends FiatTransactionModal
	implements BpModalCanDismiss, OnInit, AfterViewInit, OnDestroy
{
	public availableProviders: BehaviorSubject<PaymentOption[]> = new BehaviorSubject([]);
	public unavailableProviders: BehaviorSubject<PaymentOption[]> = new BehaviorSubject([]);

	private pinConfirmSuccess = false;
	private fail: boolean = false;
	protected label = "deposit";
	public pendingModal = null;
	public tab: string = "";
	public currentBestRewardLevel: number;
	public fiats: Fiat[] = [];

	private eligibleProviderForFiat: BehaviorSubject<PaymentOption[]> = new BehaviorSubject([]);
	private providerFee: Fee = new Fee();
	private paymentOptionsUpdated: boolean = false;
	private dryRunResult: BestDepositDiscountModel = null;
	private pageChangeSubject: Subject<void> = new Subject();
	private flowStartTracked: boolean = false;

	protected readonly flowName: string = "Deposit";
	protected readonly pathPrepend: string = "/deposit/fiat";

	public depositSurveyLink: string;

	public constructor(
		private tradableListService: TradableListService,
		private cryptocoinService: CryptocoinService,
		private ref: ChangeDetectorRef,
		private translateService: TranslationService,
		protected confirmModalService: ConfirmModalService,
		private accountService: AccountService,
		private depositSurveyService: DepositSurveyService,
		private bestFacade: BestFacade,
		private masterDataService: MasterdataService,
		private verificationModalFacade: VerificationModalFacade
	) {
		super(confirmModalService);
	}

	public ngOnInit(): void {
		this.fiats = this.fiatService.list;
		this.subscribeBestReward();
		this.initTracking();
		this.onDepositSurveyLinkChange();
	}

	private onDepositSurveyLinkChange(): void {
		this.depositSurveyService
			.getDepositSurveyLink$()
			.pipe(untilDestroyed(this))
			.subscribe((link: string) => {
				this.depositSurveyLink = link;
			});
	}

	private subscribeBestReward(): void {
		this.bestFacade
			.getBestState$()
			.pipe(
				filter((state: BestModel) => !!state),
				untilDestroyed(this)
			)
			.subscribe((result: BestModel) => {
				this.currentBestRewardLevel = result?.current_vip_levels_threshold?.level;
				this.masterDataService.markPaymentOptionsAsOutdated();

				if (this.data.transaction.getFiatWalletId() !== "") {
					this.selectWallet(this.fiatwalletService.getItemById(this.data.transaction.getFiatWalletId()));
				}
			});
	}

	public ngOnDestroy(): void {
		this.pageChangeSubject.complete();
	}

	private isPaymentOptionsRefreshNeeded(): boolean {
		return (
			this.masterdataService.isPaymentOptionsRefreshNeeded || this.masterdataService.payment_options.length === 0
		);
	}

	private updatePaymentOptions(): void {
		// TODO: temp solution - will be corrected with the payments module slicing (in the next 2-3 sprints)
		if (!this.isPaymentOptionsRefreshNeeded()) {
			this.updateProviderChanges();
			return;
		}

		this.masterdataService.refreshPaymentOptions().subscribe({
			next: () => {
				this.updateProviderChanges();
				this.pageChangeSubject.next();
			},
			error: () => {
				this.pageChangeSubject.next();
			}
		});
	}

	private updateProviderChanges(): void {
		this.masterdataService.checkLimits();
		this.setProvider();
		const eligibleProviders: PaymentOption[] = this.masterdataService.payment_options
			.filter((po: PaymentOption) => po.isEnabled())
			.filter((po: PaymentOption) =>
				po.getFees().some((f: Fee) => f.getFiatId() === this.fiat.getId() && f.getFeeDifference() !== 0)
			);
		this.eligibleProviderForFiat.next(eligibleProviders);
		this.paymentOptionsUpdated = true;
	}

	public arePaymentOptionsUpdated(): boolean {
		return this.paymentOptionsUpdated;
	}

	private setProviderFee(): void {
		this.providerFee = this.provider.getFees().find((f: Fee) => f.getFiatId() === this.fiat.getId()) || new Fee();
	}

	public getAvailableProviders$(): Observable<PaymentOption[]> {
		return this.availableProviders.asObservable();
	}

	public getUnavailableProviders$(): Observable<PaymentOption[]> {
		return this.unavailableProviders.asObservable();
	}

	public getEligibleProviderForFiat$(): Observable<PaymentOption[]> {
		return this.eligibleProviderForFiat.asObservable();
	}

	public showFeeAdditionalInfo(forFix: boolean = true): boolean {
		if (forFix) {
			return this.getProviderFee().getFeeFix() !== 0;
		}
		return this.getProviderFee().getFeeMax() !== 0;
	}

	public showFee(): boolean {
		return this.getProviderFee().getFeePercent() !== 0;
	}

	public switchType(): void {
		this._toggleUserWantsType();
		this.rudderstackTrack("Deposit Fee Selection", {
			fees_selection: this.data.transaction.getUserWantsType(),
			flow: this.flowName
		});
	}

	private _toggleUserWantsType(): void {
		this.data.transaction.getUserWantsType() === "gross"
			? this.data.transaction.setUserWantsType("net")
			: this.data.transaction.setUserWantsType("gross");
	}

	public getProviderFee(): Fee {
		// Since we select providers AFTER selecting FIAT, we should ALWAYS find a provider-fee with the correct fiat-id
		return this.providerFee;
	}

	public get amount(): number {
		return isNaN(this.data.transaction.getAmount()) ? 0 : this.data.transaction.getAmount();
	}

	protected checkedSufficientFunds(): void {
		const feeExcl: boolean = this.data.transaction.getUserWantsType() === "net";
		const dryRunModel: BestDepositDiscountModel = new BestDepositDiscountModel(
			"" + this.amount,
			this.data.transaction.getPaymentOptionId(),
			this.fiat.getId(),
			"" + this.getProviderFee().getFeePercent(),
			"" + this.getProviderFee().getFeeFix(),
			"" + this.getProviderFee().getFeeMax(),
			feeExcl
		);

		const options = new ApicallOption(BestDepositDiscountModel, dryRunModel);
		options.apiVersion = "v2";

		this.apicallService.post("fiatwallet/" + this.wallet.getId() + "/deposit/dry-offer", options).subscribe(
			(res) => {
				this.dryRunResult = res[0];
				this.data.transaction.setFee(+this.dryRunResult.getFeeAmount());
				this.data.transaction.setBfcDifferenceAmount(this.dryRunResult.getBfcDifferenceAmountFiat());
				this.data.transaction.setAmount(this.dryRunResult.getAmount());
				this.moveToSummary();
				this.pageChangeSubject.next();
			},
			(error) => {
				if (error instanceof ErrorClass) {
					this.infoService.setError(error);
					if (error instanceof ForbiddenError) {
						this.closeErrorFlow();
					} // Closes everything when forbidden error is received
				} else {
					console.error(error);
				}
			}
		);
	}

	public modalCanDismiss(): boolean | Observable<boolean> {
		if (this.fail || this.data.transaction.status === "waiting_for_payment") {
			return true;
		}

		return super.modalCanDismiss();
	}

	public getFeeByFiatId(p: PaymentOption): Fee {
		return p.getFees().find((f: Fee) => f.getFiatId() === this.fiat.getId());
	}

	public get isHeaderOk(): boolean {
		return this.isEdit(0) && this.preselected != null && !this.isUnverified;
	}

	public get modIceBlue(): boolean {
		return !this.isUnverified && !this.isTradableListSelection();
	}

	public get isUnverified(): boolean {
		return this.userService.isUnverified;
	}

	public isVerificationMandatory(): boolean {
		return this.userService.isVerificationMandatory;
	}

	public isVerificationPending(): boolean {
		return this.userService.isVerificationPending();
	}

	public isDepositDiscountActive(): boolean {
		return this.getEligibleProviderForFiat().length > 0;
	}

	public getFeeTypeLabel(isTotalAmount: boolean = true): string {
		if (isTotalAmount) {
			return this.data.transaction.getUserWantsType() === "gross" ? "inkl" : "exkl";
		}
		return this.data.transaction.getUserWantsType() === "gross" ? "inkl_fees_translation" : "exkl_fees_translation";
	}

	public getEligibleProviderForFiat(): PaymentOption[] {
		return this.eligibleProviderForFiat.getValue();
	}

	private _getFeeVipDifference(p: PaymentOption): string {
		const fee: Fee = this.getFeeByFiatId(p);

		return fee ? truncateFixed(fee.getFeeDifference(), 0) : "";
	}

	public getBestFeeString(provider: PaymentOption): string {
		const lowerFees = this.translateService.translate("best_fee_collection_lower_fees");

		return `${this._getFillerTranslation(provider)} ${this._getFeeVipDifference(
			provider
		)}% ${lowerFees} ${provider.getName()}`;
	}

	private _getFillerTranslation(p: PaymentOption): string {
		const index: number = this.getEligibleProviderForFiat().findIndex((o) => o.getId() === p.getId());
		if (index === 0) {
			return "";
		}

		if (index === this.getEligibleProviderForFiat().length - 1) {
			return this.translateService.translate("best_fee_collection_and");
		}

		return ", ";
	}

	public async selectProvider(paymentOption: PaymentOption): Promise<void> {
		if (this.edit > 0 && this.data.transaction.amount.length > 0) {
			if (this.provider != null && this.provider.getId() === paymentOption.getId()) {
				this.summary = true;
				this.pageChangeSubject.next();
				return;
			}
		}

		return this.apicallService
			.post(
				"user/limits",
				new ApicallOption(Limit, {
					type: "deposit",
					payment_option_id: paymentOption.getId(),
					fiat_id: this.fiat.getId()
				})
			)
			.toPromise()
			.then(
				(limits: Limit[]) => this.onProviderSelected(limits, paymentOption),
				(e) => Helper.handleError(e)
			);
	}

	public onProviderSelected(limits: Limit[], paymentOption: PaymentOption): void {
		this.trackSelect("payment");
		this.edit = 0;
		this.provider = paymentOption;
		this.limit = limits[0];
		this.setProviderFee();
		this.doTrapFocusTime();
		this.pageChangeSubject.next();
	}

	public selectWallet(fw: FiatWallet, newSelectedProvider?: PaymentOption): void {
		this.trackSelect("fiat");
		this.paymentOptionsUpdated = false;
		if (this.wallet == null || this.data.transaction.getDirectFiatTransaction()) {
			if (this.wallet != null) {
				this.data.transaction.setDirectFiatTransaction(false);
			}
			this.wallet = fw;
			this.preselected = null;
			this.updatePaymentOptions();
			this.doTrapFocusTime();
			return;
		}
		this.edit = 0;

		if (this.wallet.getId() === fw.getId() && !!this.provider && !!this.data.transaction.amount) {
			this.summary = true;
			this.doTrapFocusTime();
			this.pageChangeSubject.next();
			return;
		}

		this.wallet = fw;
		this.updatePaymentOptions();

		if (
			!newSelectedProvider &&
			!!this.provider &&
			!this.provider.getFiatIDs().some((fiatId: string) => fiatId === this.wallet.getFiatId())
		) {
			this.provider = new PaymentOption();
		}
		this.doTrapFocusTime();
		this.pageChangeSubject.next();
	}

	public set wallet(wallet: FiatWallet) {
		this.setWalletById(wallet.getId());
	}

	public get wallet(): FiatWallet {
		return this.fiatwalletService.getItemById(this.data.transaction.getFiatWalletId());
	}

	public pinConfirmed(t: FiatTransaction): void {
		if (!t) {
			this.fail = true;
			this.closeAll();
			return;
		}
		this.track("pin_verified");
		// Needed both!!!

		this.pinConfirmSuccess = true;

		this.data.transaction = t;

		this.data.transaction.requires_2fa_approval = false;
		this.data.transaction.status = "waiting_for_payment";

		if (this.provider.isWebHandled(config.appUrl, t.getPaymentLink())) {
			this.goTo3DS();
			return;
		}

		this.gotoPaymentUrl();
	}

	public closeErrorFlow(): void {
		this.fail = true;
		this.close();
	}

	public goTo3DS(): void {
		this.data.transaction.dirty = false;
		this.close(false);

		this.pendingModal = this.modalService.create(
			Paysafe3DSModalComponent,
			{ transaction: this.data.transaction },
			{ preventBackdropDismiss: true }
		);
		this.pendingModal.present();
	}

	public gotoPaymentUrl(): void {
		this.data.transaction.dirty = false;
		this.close(false);
		this.pendingModal = this.modalService.create(
			DepositPendingComponent,
			{ transaction: this.data.transaction },
			{ backdropDismiss: false }
		);
		this.pendingModal.present();
	}

	public setWalletById(pid: string): void {
		this.data.transaction.setFiatWalletId(pid);
		this.data.transaction.setFiatId(this.fiat.getId());
		this.setProvider();
	}

	public get wallets(): FiatWallet[] {
		return this.fiatwalletService.list;
	}

	public get fiat(): Fiat {
		if (!this.wallet) {
			return new Fiat();
		}
		return this.fiatService.getItemById(this.wallet.getFiatId());
	}

	public get provider(): PaymentOption {
		return this.masterdataService.getPaymentOptionById(this.data.transaction.getPaymentOptionId());
	}

	public set provider(p: PaymentOption) {
		this.data.transaction.setPaymentOptionId(p.getId());
	}

	public get providerTooltip(): string {
		return this.translationService.translate("unavailable_because_of_fiat");
	}

	public setEdit(e: number, summary = false): void {
		if (e === 3 && this.data.transaction.getUserWantsType() === "net") {
			this.data.transaction.setAmount(this.dryRunResult.getAmountExclFee());
		}
		super.setEdit(e, summary);
		this.ref.detectChanges(); // Refreshes the rendering
		this.pageChangeSubject.next();
	}

	public stepBack(step: number): void {
		this.provider = new PaymentOption();
		switch (step) {
			case 1: {
				this.wallet = new FiatWallet();
				this.track("fiat_cancel");
				this.paymentOptionsUpdated = false;
				this.ref.detectChanges(); // Refreshes the rendering
				break;
			}
			case 2: {
				this.track("payment_back");
				break;
			}
			default:
				break;
		}
		this.doTrapFocusTime();
		this.pageChangeSubject.next();
	}

	// Summary finish:
	public setCreated(offer: FiatTransaction): void {
		if (this.provider.isWebHandled(config.appUrl, offer.getPaymentLink())) {
			this.created = true;
			this.data.transaction = offer;
			if (!this.needsVerification) {
				this.goTo3DS();
				this.saved = true;
				this.close();
			}
			this.pageChangeSubject.next();
			return;
		}

		// QR deposit modal flow for SEPA / Bank transfer
		if (this.provider.isManual) {
			this.saved = true;
			this.close();
			return;
		}

		// Regular flow
		this.created = true;
		this.data.transaction = offer;
		if (!this.needsVerification) {
			this.gotoPaymentUrl();
			this.router.navigateByUrl("fiat-wallet/" + this.wallet.getId());
			this.saved = true;
			this.close();
		} else {
			this.doTrapFocusTime();
		}
		this.pageChangeSubject.next();
	}

	public get maxAmount(): number {
		if (this.limit.getMax() > 0) {
			return this.limit.getMax();
		}
		if (this.provider == null) {
			return 0;
		}
		const limit = this.provider.getLimitForFiat(this.fiat);
		if (!limit) {
			return 0;
		}
		return limit.getAmount();
	}

	public get needsVerification(): boolean {
		return this.data.transaction.needsPinConfirmation() && this.created && this.summary;
	}

	public get isFirstStep(): boolean {
		return (this.wallet == null && this.isEdit(0)) || this.isEdit(1);
	}

	public isTradableListSelection(): boolean {
		return this.isFirstStep && !this.tradableId;
	}

	public get isSecondStep(): boolean {
		return (
			(this.wallet != null && this.provider == null && this.arePaymentOptionsUpdated() && this.isEdit(0)) ||
			this.isEdit(2)
		);
	}

	public get isThirdStep(): boolean {
		return (this.provider != null && this.isEdit(0)) || this.isEdit(3);
	}

	public isViewVisible(): boolean {
		return this.isFirstStep || this.isSecondStep || this.isThirdStep;
	}

	public verify(): void {
		if (!this.verificationModalFacade.openVerification()) {
			this.router.navigate(VERIFICATION_MODAL_ROUTE, {
				skipLocationChange: true
			});
		}
	}

	private setProvider(): void {
		const availableProviders: PaymentOption[] = [];
		const unavailableProviders: PaymentOption[] = [];

		this.masterdataService.payment_options.filter(this._optionsIsVisible).forEach((option: PaymentOption) => {
			if (option.hasFiat(this.fiat)) {
				availableProviders.push(option);
			} else {
				unavailableProviders.push(option);
			}
		});
		this.availableProviders.next(availableProviders);
		this.unavailableProviders.next(unavailableProviders);
	}

	private _optionsIsVisible(option: PaymentOption): boolean {
		return !option.is_voucher && option.isEnabled() && option.hasSupportedFinalizationType;
	}

	public close(abort: boolean = true): void {
		if (this.needsVerification) {
			this.track("pin_cancel");
			super.close(false);
		} else {
			super.close(abort);
		}
	}

	/* Crypto Wallets Deposit code: */

	public getDepositTradables(): Cryptocoin[] {
		return this.cryptocoinService.list.filter((coin) => coin.isDepositActive());
	}

	public visibleWallets(): Observable<TradableWallet[]> {
		return this.walletService.getList().pipe(
			map((list: TradableWallet[]) =>
				list.filter((w) => {
					const tradable: Tradable = this.tradableListService.getItemById(w.getCurrencyId());
					if (!tradable) {
						return false;
					} // Will jump out if a wallet is detected that has an incorrect currencyId -> no asset could be found
					return !w.isDeleted() && tradable.isDepositActive() && !tradable.isIndexOnly();
				})
			)
		);
	}

	// BEST 2.0 opt-in terms
	public navigateToWallet(wallet: TradableWallet): void {
		if (wallet.getCurrencyId() === TOKEN_BEST_ID && this.accountService.showBest2TermsOnBestTrade()) {
			this.handleBest2Terms(wallet);
			return;
		}

		this.closeAll();
		this.modalService
			.create(CryptowalletDetailsComponent, {
				data: { wallet: wallet, cryptocoinIcon: this.data.cryptocoinIcon }
			})
			.present();
	}

	// BEST 2.0 opt-in terms
	public handleBest2Terms(wallet: TradableWallet): void {
		this.modalService.getCurrentModal().hide();
		const best2TermsModal = this.modalService.create(Best2TermsModalComponent, null, { showBackdrop: false });
		best2TermsModal.present();
		best2TermsModal.onDismiss.subscribe((res) => {
			if (res.data === "accepted") {
				this.navigateToWallet(wallet);
			} else {
				this.tradableId = null;
			}
		});
	}

	public get currentStep(): ModalHeader {
		const header: ModalHeader = this.getDefaultHeader(this.translate("deposit_choose_wallet_header"));

		if ((this.isSecondStep && this.isEdit(0)) || (this.isThirdStep && !this.isEdit(3))) {
			header.left = "back";
		} else if ((this.isFirstStep && (this.isHeaderOk || this.isEdit(1))) || (this.isSecondStep && this.isEdit(2))) {
			header.left = "abort";
		}
		if ((this.isFirstStep && (this.isEdit(1) || this.isHeaderOk)) || (this.isSecondStep && this.isEdit(2))) {
			header.right = "ok";
		} else if (
			(this.isFirstStep && !this.isHeaderOk && !this.isEdit(1)) ||
			(this.isSecondStep && !this.isEdit(2)) ||
			this.isThirdStep
		) {
			header.right = "abort";
		}

		return header;
	}

	public onHeaderButtonClick(name: string): void {
		if (name === "back") {
			this.isSecondStep ? this.stepBack(1) : this.stepBack(2);
		} else if (name === "abort") {
			this.close();
		} else if (name === "ok" && this.isHeaderOk) {
			this.selectWallet(this.preselected as FiatWallet);
		} else {
			super.onHeaderButtonClick(name);
		}
	}

	public initTracking(): void {
		this.pageChangeSubject
			.pipe(
				finalize(() =>
					this.rudderstackFlowState(this.saved || this.pinConfirmSuccess ? "successful" : "cancelled")
				)
			)
			.subscribe(() => {
				if (this.needsVerification) {
					this.rudderstackPageFlow("PIN", "pin", {
						fiat_currency: this.fiat.getSymbol(),
						deposit_method: this.provider.getShortName(),
						deposit_amount: "" + this.amount
					});
				} else if (this.summary) {
					this.rudderstackPageFlow("Summary", "summary", {
						fiat_currency: this.fiat.getSymbol(),
						deposit_method: this.provider.getShortName(),
						deposit_amount: "" + this.amount
					});
				} else if (this.isThirdStep) {
					this.rudderstackPageFlow("Amount", "amount", {
						fiat_currency: this.fiat.getSymbol(),
						deposit_method: this.provider.getShortName()
					});
				} else if (this.isSecondStep) {
					if (!this.flowStartTracked) {
						this.rudderstackFlowState("started");
						this.flowStartTracked = true;
					}
					this.rudderstackPageFlow("Method Selection", "method", {
						fiat_currency: this.fiat.getSymbol()
					});
				} else if (this.isFirstStep) {
					if (this.tab === "crypto") {
						this.rudderstackPage("Deposit Asset Currency Selection", "/deposit/asset");
					} else {
						this.rudderstackPageFlow("Currency Selection");
					}
				}
			});

		this.pageChangeSubject.next();
	}

	// Left mouse button + touchpad
	public onClickDepositSurveyLink(): void {
		this.depositSurveyService.saveVisitingSurvey();
	}

	// Middle mouse button (scroll)
	public onMousedownDepositSurveyLink(event: MouseEvent): void {
		if (event.button === 1) {
			// Without setTimeout browser does not perform default event (opening in new tab)
			setTimeout(() => {
				this.depositSurveyService.saveVisitingSurvey();
			}, 200);
		}
	}
}

class BestDepositDiscountModel {
	private amount: string = "";
	private amount_excl_fee: string = "";
	private payment_option_id: string = "";
	private fiat_id: string = "";
	private expected_fee_percent: string = "";
	private expected_fee_fix: string = "";
	private expected_fee_max: string = "";
	private fee: string = "";
	private best_vip_fee_difference = "";
	private is_fee_excl: boolean = false;

	public constructor(
		amount: string,
		paymentOptionId: string,
		fiatId: string,
		expectedFeePercent: string,
		expectedFeeFix: string,
		expectedFeeMax: string,
		isFeeExcl: boolean
	) {
		this.amount = amount;
		this.payment_option_id = paymentOptionId;
		this.fiat_id = fiatId;
		this.expected_fee_percent = expectedFeePercent;
		this.expected_fee_fix = expectedFeeFix;
		this.expected_fee_max = expectedFeeMax;
		this.is_fee_excl = isFeeExcl;
	}

	public getFeeAmount(): string {
		return this.fee;
	}

	public getAmountExclFee(): string {
		return this.amount_excl_fee;
	}

	public getBfcDifferenceAmountFiat(): string {
		return this.best_vip_fee_difference;
	}

	public getAmount(): string {
		return this.amount;
	}
}
